import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CampaignRanking } from '../../types/campaignRanking';

interface CampaignScorecardModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: CampaignRanking;
}

export const CampaignScorecardModal: React.FC<CampaignScorecardModalProps> = ({ isOpen, onClose, campaign }) => {
  const { performance, execution } = campaign;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold font-['Hanken_Grotesk'] text-slate-900">
            {campaign.name} Scorecard
          </DialogTitle>
          <div className="text-sm text-slate-500">
            Overall Score: <span className="font-semibold text-slate-800">{campaign.overallScore}%</span> 
            <span className="mx-2">•</span>
            Tier: <span className="font-semibold text-slate-800">{campaign.tier}</span>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          {/* Performance Column */}
          <div className="space-y-4">
            <h3 className="font-bold text-slate-800 border-b pb-2">
              Performance (75%)
              <span className="float-right text-[#A655F8]">{(performance.totalScore * 0.75).toFixed(1)}%</span>
            </h3>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Engagement Rate (45% weight)</span>
                <span className="font-semibold text-slate-800">{(performance.engagementScore * 0.45 * 0.75).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Total Views (35% weight)</span>
                <span className="font-semibold text-slate-800">{(performance.viewsScore * 0.35 * 0.75).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Total Engagement (20% weight)</span>
                <span className="font-semibold text-slate-800">{(performance.engagementVolumeScore * 0.20 * 0.75).toFixed(1)}%</span>
              </div>
            </div>
            
            <div className="bg-slate-50 p-3 rounded text-xs text-slate-500 mt-4 space-y-1">
              <div><span className="font-semibold text-slate-700">Raw ER:</span> {performance.engagementRate.toFixed(2)}%</div>
            </div>
          </div>

          {/* Execution Column */}
          <div className="space-y-4">
            <h3 className="font-bold text-slate-800 border-b pb-2">
              Execution (25%)
              <span className="float-right text-[#A655F8]">{(execution.totalScore * 0.25).toFixed(1)}%</span>
            </h3>
            
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Deliverable Completion (40% weight)</span>
                <span className="font-semibold text-slate-800">{(execution.deliverableCompletionScore * 0.40 * 0.25).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Timeline Adherence (20% weight)</span>
                <span className="font-semibold text-slate-800">{(execution.timelineAdherenceScore * 0.20 * 0.25).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Budget Management (20% weight)</span>
                <span className="font-semibold text-slate-800">{(execution.budgetManagementScore * 0.20 * 0.25).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Payment Discipline (20% weight)</span>
                <span className="font-semibold text-slate-800">{(execution.paymentDisciplineScore * 0.20 * 0.25).toFixed(1)}%</span>
              </div>
            </div>
            
            <div className="bg-slate-50 p-3 rounded text-xs text-slate-500 mt-4 space-y-1">
              <div><span className="font-semibold text-slate-700">Contracted Deliverables:</span> {campaign.metrics.contractedDeliverables}</div>
              <div><span className="font-semibold text-slate-700">Completed Deliverables:</span> {campaign.metrics.completedDeliverables}</div>
              <div><span className="font-semibold text-slate-700">Timeline Progress:</span> {Math.round(campaign.metrics.timelineProgress)}%</div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
